import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import CustomScrollbars from '../../../components/structure/CustomScrollbars';
import { color_shades_dark, color_shades_darkest, color_shades_lighter, color_text_light } from '../../../constants/colors';
import LiveChatBubble from './LiveChatBubble';
import Button from '../../../components/general/Button';
import SessionAnalyticsContainer from '../../../hooks/SessionAnalyticsContainer';
import PageError from '../../../components/general/PageError';
import Loader from '../../../components/general/Loader';
import AnalyticsOrderStateContainer from '../../../hooks/AnalyticsOrderStateContainer';
import TooltipWrapper from '../../../components/general/TooltipWrapper';
import ConfirmationDialog from '../../../components/structure/ConfirmationDialog';
import WarningConfirmation from '../../../components/general/WarningConfirmation';
import { breakpoint_small } from '../../../constants/breakpoints';
import useIsMobile from '../../../hooks/UseIsMobile';
import TestCase from '../../../models/customAssistant/TestCase';
import TestCaseContainer from '../../../hooks/TestCaseContainer';
import TestCaseEditingModal from '../../../components/forms/EditTestCase';
import SparkContainer from '../../../hooks/SparkContainer';
import SessionTags from '../../../components/analytics/SessionTags';
import SessionAppointmentReviewSummary from '../../../components/analytics/SessionAppointmentReviewSummary';
import { FoodOrderingFeatureFlagId, HealthcareSchedulingFeatureFlagId, GenAIFoodOrdering } from '../../../constants/featureFlagIds';
import ConversationalSuccessEvent from '../../../models/analytics/ConversationalSuccessEvent';
import ApplicationContainer from '../../../state/containers/ApplicationContainer';
import ApplicationModel from '../../../models/applications/api/ApplicationModel';
import SessionAppointmentReviewDataModel from '../../../models/analytics/api/SessionAppointmentReviewDataModel';
import moment from 'moment';
import { REVIEW_SESSION_DATE_FORMAT } from '../../../models/analytics/AnalyticsDateFormat';
import * as voicifyApi from '../../../api';
import { getOrderFailureReasonMarkdown } from './orderFailureDiagnostics';

const redXIcon = require('../../../content/images/spark/red-x.svg');
const greenCheckIcon = require('../../../content/images/spark/green-check.svg');
const copyIcon = require('../../../content/images/copy-text.svg');

type OrderState = "orderPlaced" | "orderNotPlaced" | string;

interface SessionReviewProps {
    applicationId: string,
    setEventsToShow: (events: any[]) => void
    events: any[],
    manualHeight?: any,
    hideBorder?: boolean,
    simple?: boolean,
    inModal?: boolean
    resetCopyButtonText?: () => void
    sessionId?: string
    forcedHeight?: number,
    superSimple?: boolean
    mobileBubbles?: boolean
    requireSpeech?: boolean,
    userBubbleStyle?: string
    showFrom?: boolean
    appContainer?: ApplicationContainer
    application?: ApplicationModel
    tags?: string[]
    showConversationCalls?: boolean
    onSessionSelect?: (sessionId: string) => void
}

interface ConversationSession {
    sessionId: string
    startDate?: Date
    from?: string
}

const TagsWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    flex: 1;
    min-width: 0; // Allows flex item to shrink
    
    ${breakpoint_small} {
        width: 100%;
    }
`;

const StyledSessionTags = styled(SessionTags)`
    margin: 6px 0 0 0;
    
    // In TagsWrapper (advanced view), reset margin and allow natural flex behavior
    ${TagsWrapper} & {
        margin: 0;
        width: 100%;
    }
`;

const StyledSessionAppointmentReviewSummary = styled(SessionAppointmentReviewSummary)`
    ${TagsWrapper} & {
        margin: 6px 0 0 0;
        width: 100%;
    }
`;

const InlineSessionMetaRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    ${breakpoint_small} {
        align-items: flex-start;
        flex-direction: column;
        gap: 4px;
    }
`;

const InlineSessionAppointmentReviewSummary = styled(SessionAppointmentReviewSummary)`
    margin: 0;
`;

const ButtonsWrapper = styled.div`
    display: flex;
    flex-shrink: 0;
    justify-content: flex-start;
    
    ${breakpoint_small} {
        flex-wrap: wrap;
        width: 100%;
        justify-content: flex-start;
    }
`;

const RequestDetails = styled.div``;

const RelativeWrapper = styled.div`
    background-color: ${color_shades_lighter};
    &.in-modal {
        padding-top: 16px;
    }
`;

const HistoryBody = styled(CustomScrollbars) <{ superSimple?: boolean }>`
    color: ${color_shades_darkest};
    background-color: ${color_shades_lighter};
    ${(props) => props.superSimple ?
        `` : `    margin-top: 16px;
    margin-bottom: 16px;`
    }

    &.in-modal {
        margin-top: 0px;
        margin-bottom: 0px;    
    }
    ${breakpoint_small} {
        width: 100%;
    }
`;

const AllConversationsContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0px 16px 16px 16px;
    overflow-wrap: break-word;
    word-break:break-word;
    &.scrollable {
        padding: 0px 16px 16px 16px;
    }
`;

const HiddenDiv = styled.div`
    display: none;
`;

const TopLevelDetailsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0px;
    margin-left: 44px;
    
    ${breakpoint_small} {
        gap: 0px;
        margin-left: 0;
    }
`;

const StyledButton = styled(Button)`
    width: 186px;
    margin-right: 8px;
    margin-left: 8px;
    
    &:first-of-type {
        margin-left: 4px;
    }
    
    ${breakpoint_small} {
        width: auto;
        min-width: 120px;
        margin: 4px;
        font-size: 12px;
        padding: 0 16px;
        
        &:first-of-type {
            margin-left: 4px;
        }
    }
`;

const PhoneNumberWrapper = styled.p`
    margin: 0 0 0 4px;
    color: ${color_text_light};
    font-size: 16px;
    flex-shrink: 0;
    white-space: nowrap;
    
    ${breakpoint_small} {
        font-size: 14px;
        margin: 0;
    }
`;

const PhoneNumberRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;

    ${breakpoint_small} {
        gap: 4px;
    }
`;

const PhoneCopyButton = styled.button<{ copied?: boolean }>`
    border: none;
    background: transparent;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: ${props => props.copied ? 1 : 0.75};

    &:hover {
        opacity: 1;
    }
`;

const PhoneCopyIcon = styled.img`
    width: 13px;
    height: 13px;
    display: block;
`;

const SessionPhoneAndTagsWrapper = styled.div`
    flex-wrap: wrap;
    display: flex;
    flex-direction: column;
`;

const SparkInlineWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
    
    ${breakpoint_small} {
        flex-direction: column;
        gap: 6px;
    }
`;

const PhoneAndTagsRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-wrap: wrap;
    
    ${breakpoint_small} {
        flex-direction: column;
        gap: 4px;
    }
`;

const ConversationCallsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0 0 12px 44px;
    ${breakpoint_small} {
        margin: 0 0 12px 0;
    }
`;

const ConversationCallsHeader = styled.div`
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: ${color_text_light};
`;

const ConversationCallsList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0;
    margin-left: -16px;
`;

const ConversationCallsLoader = styled.div`
    display: flex;
    align-items: center;
    padding: 4px 0;
`;

const ConversationCallPill = styled(Button) <{ active?: boolean, clickable?: boolean }>`
    border-radius: 14px;
    padding: 4px 12px;
    font-size: 12px;
    font-weight: 600;
    margin-right: -4px;
    cursor: ${props => props.clickable ? 'pointer' : 'default'};
    ${props => props.active ? `border-color: ${color_shades_darkest};` : ''}
`;


const SessionReview = (props: SessionReviewProps) => {
    const [recordingsAvailable, setRecordingsAvailable] = useState(true);
    const [showOrderPlacedConfirmationModal, setShowOrderPlacedConfirmationModal] = useState(false);
    const [orderState, setOrderState] = useState<OrderState>();
    const [requestIdEvents, setRequestIdEvents] = useState<any[]>([]);
    const [groupedEvents, setGroupedEvents] = useState<any>({});
    const [copyButtonText, setCopyButtonText] = useState("Copy Transcript");
    const [fromNumber, setFrom] = useState("");
    const [sessionTags, setSessionTags] = useState<string[]>([]);
    const [featureFlag, setFeatureFlag] = useState<string | undefined>(undefined);
    const [showEditTestCaseModal, setShowEditTestCaseModal] = useState(false);
    const [testCaseToEdit, setTestCaseToEdit] = useState({});
    const [contentItemId, setContentItemId] = useState('');
    const [conversationSessions, setConversationSessions] = useState([] as ConversationSession[]);
    const [conversationCallsLoading, setConversationCallsLoading] = useState(false);
    const [conversationSwitchingSessionId, setConversationSwitchingSessionId] = useState<string | null>(null);
    const [appointmentReviewData, setAppointmentReviewData] = useState<SessionAppointmentReviewDataModel | null>(null);
    const [appointmentReviewLoading, setAppointmentReviewLoading] = useState(false);
    const [copiedPhoneNumber, setCopiedPhoneNumber] = useState(false);
    const lastConversationKeyRef = useRef('');
    const lastLoadedSessionIdRef = useRef<string | null>(null);
    const lastLoadedAppointmentReviewSessionIdRef = useRef<string | null>(null);
    const loadedConversationSessionIdsRef = useRef(new Set<string>());
    const fallbackAttemptedSessionIdsRef = useRef(new Set<string>());
    const phoneCopyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const sessionContainer = SessionAnalyticsContainer.useContainer();
    const testCaseContainer = TestCaseContainer.useContainer();
    const analyticsOrderStateContainer = AnalyticsOrderStateContainer.useContainer();
    const sparkContainer = SparkContainer.useContainer();
    const isMobile = useIsMobile();

    useEffect(() => {
        if (copyButtonText === "Copied!") {
            setCopyButtonText("Copy Transcript");
        }
    }, [props.events, props.applicationId])

    useEffect(() => {
        let set = false;
        if (props.events?.length) {
            const contentHits = props.events.filter(e => e.eventType === "ContentHit" && e?.originalRequest?.sessionAttributes?.callingNumber?.length);
            if (contentHits?.length) {
                const callingNumber = contentHits[0]?.originalRequest?.sessionAttributes?.callingNumber;
                if (callingNumber) {
                    setFrom(callingNumber);
                    set = true;
                }
            }
        }
        if (!set) {
            setFrom("");
        }
    }, [props.events]);

    useEffect(() => {
        const flag = getFeatureFlag();
        setFeatureFlag(flag);
    }, [props.applicationId, props.application?.featureFlags]);

    useEffect(() => {
        setRequestIdEvents(props.events.filter(e => e.requestId?.length).filter(e => e.eventType !== "ConversationalSuccess"));
    }, [props.events]);

    useEffect(() => {
        if (props.tags) {
            setSessionTags(props.tags);
        } else {
            const extractedTags = extractTagsFromEvents(props.events);
            setSessionTags(extractedTags);
        }
    }, [props.events, props.tags]);

    useEffect(() => {
        const groupedEvents = requestIdEvents.reduce((acc, event) => {
            if (event.requestId in acc) {
                acc[event.requestId].push(event);
            } else {
                acc[event.requestId] = [event];
            }
            return acc;
        }, {} as { [requestId: string]: any[] });
        setGroupedEvents(groupedEvents);
    }, [requestIdEvents]);

    useEffect(() => {
        let sessionIdToLoad: string | null = null;
        if (props.sessionId) {
            sessionIdToLoad = props.sessionId;
        } else {
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);
            sessionIdToLoad = params.get('sessionId');
        }
        if (!sessionIdToLoad?.length) return;
        if (lastLoadedSessionIdRef.current === sessionIdToLoad) return;
        lastLoadedSessionIdRef.current = sessionIdToLoad;
        loadEventsForSession(sessionIdToLoad);
    }, [window.location.href, props.sessionId]);

    useEffect(() => {
        return () => {
            if (phoneCopyTimeoutRef.current !== null) {
                clearTimeout(phoneCopyTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        let sessionIdToLoad: string | null = null;
        if (props.sessionId) {
            sessionIdToLoad = props.sessionId;
        } else {
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);
            sessionIdToLoad = params.get('sessionId');
        }

        if (!sessionIdToLoad?.length) {
            setAppointmentReviewData(null);
            setAppointmentReviewLoading(false);
            lastLoadedAppointmentReviewSessionIdRef.current = null;
            return;
        }

        if (lastLoadedAppointmentReviewSessionIdRef.current === sessionIdToLoad) {
            return;
        }

        lastLoadedAppointmentReviewSessionIdRef.current = sessionIdToLoad;
        loadAppointmentReviewData(sessionIdToLoad);
    }, [window.location.href, props.sessionId, props.applicationId]);

    useEffect(() => {
        if (props.showConversationCalls === false) {
            setConversationSessions([]);
            setConversationCallsLoading(false);
            setConversationSwitchingSessionId(null);
            return;
        }
        if (!props.events?.length) {
            return;
        }
        const getEventTime = (event): number | undefined => {
            if (event?.eventDate) {
                const time = new Date(event.eventDate).getTime();
                if (!isNaN(time)) return time;
            }
            if (event?.timestamp) {
                const ts = typeof event.timestamp === 'number' ? event.timestamp : new Date(event.timestamp).getTime();
                if (!isNaN(ts)) return ts;
            }
            return undefined;
        };
        const getEarliestEventDate = (events: any[]): Date | undefined => {
            if (!events?.length) return undefined;
            let earliest: number | undefined = undefined;
            events.forEach((event) => {
                const time = getEventTime(event);
                if (time !== undefined && (earliest === undefined || time < earliest)) {
                    earliest = time;
                }
            });
            return earliest !== undefined ? new Date(earliest) : undefined;
        };
        const getFromNumber = (events: any[]): string | undefined => {
            if (!events?.length) return undefined;
            const eventWithFrom = events.find(e => e?.from?.length || e?.phoneNumber?.length || e?.originalRequest?.sessionAttributes?.callingNumber?.length);
            return eventWithFrom?.from || eventWithFrom?.phoneNumber || eventWithFrom?.originalRequest?.sessionAttributes?.callingNumber;
        };
        const getSessionSummariesValue = (events: any[]): any => {
            const engagementMatch = events.find(e => e?.eventType === "EngagementEvent" && e?.sessionSummaries);
            if (engagementMatch) {
                return engagementMatch.sessionSummaries;
            }
            const directMatch = events.find(e => e?.sessionSummaries);
            if (directMatch) {
                return directMatch.sessionSummaries;
            }
            return undefined;
        };
        const parseSessionSummaries = (sessionSummaries: any): string[] => {
            try {
                if (!sessionSummaries) return [];
                let parsed: any = sessionSummaries;
                for (let i = 0; i < 2; i++) {
                    if (typeof parsed === 'string') {
                        parsed = JSON.parse(parsed);
                    } else {
                        break;
                    }
                }
                if (Array.isArray(parsed)) {
                    return parsed.filter(id => typeof id === 'string' && id.length);
                }
                if (!parsed || typeof parsed !== 'object') return [];
                return Object.keys(parsed);
            } catch {
                return [];
            }
        };
        const normalizePhone = (input?: string): string | undefined => {
            if (!input?.length) return undefined;
            const digits = input.replace(/\D/g, '');
            if (!digits.length) return undefined;
            if (digits.length >= 10) {
                return digits.substring(digits.length - 10);
            }
            return digits;
        };
        const getSessionPhone = (session: any): string | undefined => {
            if (session?.from?.length) return session.from;
            if (session?.phoneNumbers?.length) return session.phoneNumbers[0];
            return undefined;
        };
        const buildConversation = async (sessionSummariesValue: any, currentSessionId: string) => {
            const summarySessionIds = parseSessionSummaries(sessionSummariesValue);
            const allSessionIds = new Set<string>(summarySessionIds.filter(id => id?.length));
            if (currentSessionId?.length) {
                allSessionIds.add(currentSessionId);
            }
            if (allSessionIds.size <= 1) {
                setConversationSessions([]);
                setConversationCallsLoading(false);
                return;
            }
            const sessionIdList = Array.from(allSessionIds);
            const newKey = sessionIdList.slice().sort().join('|');
            if (lastConversationKeyRef.current === newKey && conversationSessions.length > 0) {
                setConversationCallsLoading(false);
                return;
            }
            lastConversationKeyRef.current = newKey;
            setConversationCallsLoading(true);
            loadedConversationSessionIdsRef.current = new Set<string>();
            const entries = await Promise.all(sessionIdList.map(async (sessionId) => {
                let events = [] as any[];
                if (sessionId === currentSessionId) {
                    events = props.events;
                } else {
                    const existingEvents = sessionContainer.events?.[sessionId] as any[] | undefined;
                    if (existingEvents?.length) {
                        events = existingEvents;
                    } else if (loadedConversationSessionIdsRef.current.has(sessionId)) {
                        events = [];
                    } else {
                        loadedConversationSessionIdsRef.current.add(sessionId);
                        events = await sessionContainer.loadEventsForSession(props.applicationId, sessionId, true, true);
                    }
                }
                return {
                    sessionId,
                    startDate: getEarliestEventDate(events || []),
                    from: getFromNumber(events || [])
                } as ConversationSession;
            }));
            const sorted = entries.slice().sort((a, b) => {
                const aTime = a.startDate ? a.startDate.getTime() : Number.POSITIVE_INFINITY;
                const bTime = b.startDate ? b.startDate.getTime() : Number.POSITIVE_INFINITY;
                return aTime - bTime;
            });
            setConversationSessions(sorted);
            setConversationCallsLoading(false);
        };
        const tryFindEngagementFromNearbyCalls = async (currentSessionId: string) => {
            if (fallbackAttemptedSessionIdsRef.current.has(currentSessionId)) {
                return;
            }
            fallbackAttemptedSessionIdsRef.current.add(currentSessionId);
            setConversationCallsLoading(true);
            const currentStartDate = getEarliestEventDate(props.events);
            const currentFrom = normalizePhone(getFromNumber(props.events));
            if (!currentStartDate || !currentFrom) {
                setConversationSessions([]);
                setConversationCallsLoading(false);
                return;
            }
            const allSessions = sessionContainer.sessions?.length ? sessionContainer.sessions : sessionContainer.sessionsForReview;
            if (!allSessions?.length) {
                setConversationSessions([]);
                setConversationCallsLoading(false);
                return;
            }
            const windowMs = 30 * 60 * 1000;
            const candidates = allSessions.filter((session: any) => {
                if (!session?.startDate) return false;
                if (session.sessionId === currentSessionId) return false;
                const sessionPhone = normalizePhone(getSessionPhone(session));
                if (!sessionPhone || sessionPhone !== currentFrom) return false;
                const sessionTime = new Date(session.startDate).getTime();
                return Math.abs(sessionTime - currentStartDate.getTime()) <= windowMs;
            });
            for (const candidate of candidates) {
                const candidateEvents = await sessionContainer.loadEventsForSession(props.applicationId, candidate.sessionId, true, true);
                const candidateSummaries = getSessionSummariesValue(candidateEvents || []);
                if (candidateSummaries) {
                    await buildConversation(candidateSummaries, currentSessionId);
                    return;
                }
            }
            setConversationSessions([]);
            setConversationCallsLoading(false);
        };

        const sessionSummariesValue = getSessionSummariesValue(props.events);
        const currentSessionId = props.sessionId || props.events.find(e => e.sessionId)?.sessionId;
        if (!currentSessionId?.length) {
            setConversationSessions([]);
            setConversationCallsLoading(false);
            return;
        }
        if (conversationSwitchingSessionId && currentSessionId === conversationSwitchingSessionId) {
            setConversationSwitchingSessionId(null);
        }
        if (sessionSummariesValue) {
            buildConversation(sessionSummariesValue, currentSessionId);
            return;
        }
        tryFindEngagementFromNearbyCalls(currentSessionId);
    }, [props.events, props.applicationId, props.sessionId, props.showConversationCalls]);

    useEffect(() => {
        setRecordingsAvailable(true);
    }, [props.events, props.applicationId]);

    const getFeatureFlag = (): string | undefined => {
        if (props.application?.featureFlags?.some(flag => flag.id === FoodOrderingFeatureFlagId)) {
            return "FOOD_ORDERING";
        }
        if (props.application?.featureFlags?.some(flag => flag.id === HealthcareSchedulingFeatureFlagId)) {
            return "HEALTHCARE";
        }
        return undefined;
    };

    const extractTagsFromEvents = (events: any[]): string[] => {
        const conversationalSuccessEvents = events.filter(e => e.eventType === "ConversationalSuccess");
        if (conversationalSuccessEvents.length > 0) {
            const latestEvent: ConversationalSuccessEvent = conversationalSuccessEvents[conversationalSuccessEvents.length - 1];
            return latestEvent.sessionTags || [];
        } else {
            return [];
        }
    };
    const loadOrderState = (events: any[]) => {
        const itemAddedToOrderEvents = events?.find(e => e.eventType === "ItemAddedToOrder");
        if (itemAddedToOrderEvents) {
            const engagementEvent = events?.find(e => e.eventType === "EngagementEvent");
            if (engagementEvent?.sessionTags?.includes("ACTION_COMPLETED")) {
                setOrderState("orderPlaced");
            }
        }
    };

    const loadEventsForSession = async (sessionId: string, forceLoad?: boolean) => {
        let eventsForSession = [];
        if (forceLoad) {
            eventsForSession = await sessionContainer.loadEventsForSession(props.applicationId, sessionId, forceLoad);
        } else {
            eventsForSession = await sessionContainer.loadEventsForSession(props.applicationId, sessionId);
        }
        if (eventsForSession.length > 0) {
            loadOrderState(eventsForSession);
        }
    };

    const AlwaysScrollToBottom = () => {
        const elementRef = useRef<any>();
        useEffect(() => elementRef.current.scrollIntoView());
        return <div ref={elementRef} />;
    };

    const loadRecording = async (processed: boolean) => {
        const sessionId = props.events.find(e => e.sessionId?.length)?.sessionId;
        const recordingUrl = await sessionContainer.getRecordingUrl(props.applicationId, sessionId, processed);
        if (recordingUrl?.length) {
            // open url in new tab
            window.open(recordingUrl, "_blank")
        } else {
            setRecordingsAvailable(false);
        }
    };

    const copyText = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const formatPhoneNumber = (phoneNumber: string) => {
        // Remove any non-digit characters from the input number
        const cleanedNumber = phoneNumber?.replace(/\D/g, '');
        const formattedNumber = `(${cleanedNumber?.substring(1, 4)}) ${cleanedNumber?.substring(4, 7)}-${cleanedNumber?.substring(7)}`;
        return formattedNumber;
    };

    const stripSsml = (ssml: string) => {
        return ssml.replace(/<[^>]*>/g, '');
    };


    const handleCreateTestCase = async (events) => {
        const testCase = createTestCase(events);
        setTestCaseToEdit(testCase);
        setShowEditTestCaseModal(true);
    }

    const createTestCase = (events) => {
        const contentHit = events.find(e => e.eventType === "ContentHit");
        const contentItemIdEvents = events.find(e => e?.contentId?.length > 0)
        setContentItemId(contentItemIdEvents.contentId)
        if (contentHit?.originalRequest !== null) {
            const testCase: TestCase = {};
            testCase.text = contentHit.originalRequest.originalInput;
            testCase.sessionState = {
                sessionAttributes: contentHit.originalRequest.sessionAttributes,
                sessionFlags: contentHit.originalRequest.sessionFlags,
                id: contentHit.originalRequest.sessionId
            }
            testCase.userProfile = {
                userId: contentHit.originalRequest.userId,
                userAttributes: contentHit.originalRequest.userAttributes,
                userFlags: contentHit.originalRequest.userFlags
            }
            testCase.name = `New Test Case`;
            if (testCase.sessionState.sessionAttributes["conversationTurnHistoryState"]) {
                testCase.sessionState.sessionAttributes["ConversationTurnHistoryState"] = testCase.sessionState.sessionAttributes["conversationTurnHistoryState"];
                delete testCase.sessionState.sessionAttributes["conversationTurnHistoryState"];
            }
            if (contentHit?.contentItemId?.length > 0) {
            }
            return testCase
        }
        return {};
    }

    const saveTestCase = async (testCase: TestCase) => {
        await testCaseContainer.createTestCase(props.applicationId, testCase);
    }

    // Order state functions
    const markAsOrderPlaced = async () => {
        const applicationId = props.applicationId;
        let sessionId: string;
        if (props.sessionId) {
            sessionId = props.sessionId;
        } else {
            sessionId = props.events.find(e => e.sessionId?.length)?.sessionId;
        }
        const markOrderAsPlacedResponse = await analyticsOrderStateContainer.markOrderAsPlaced(applicationId, sessionId);
        if (markOrderAsPlacedResponse.resultType === "Ok") {
            setOrderState("orderPlaced");
            // store state in local storage
            const analyticsOrderState = analyticsOrderStateContainer.getStoredAnalyticsOrderState();
            if (analyticsOrderState[sessionId]) {
                analyticsOrderState[sessionId].orderPlaced = true;
            } else {
                analyticsOrderState[sessionId] = { orderPlaced: true };
            }
            analyticsOrderStateContainer.storeAnalyticsOrderState(analyticsOrderState);
        }
        toggleOrderPlacedConfirmationModal();
    };

    const toggleOrderPlacedConfirmationModal = () => {
        setShowOrderPlacedConfirmationModal(!showOrderPlacedConfirmationModal);
    };

    // Transcript generation functions
    const copyTranscript = () => {
        var transcript = "";
        Object.keys(groupedEvents).forEach((requestId, idx) => {
            const events = groupedEvents[requestId];
            const requestRecEvents = events.filter(e => e.eventType === "RequestReceived");
            const contentHitEvents = events.filter(e => e.eventType === "ContentHit");
            const userMarkdown = generateUserMarkdown(requestRecEvents, contentHitEvents, idx === 0, true);
            const botMarkdown = generateBotMarkdown(events, true);
            if (userMarkdown === "Unknown" && botMarkdown === "Unknown") {
                return;
            }
            if (userMarkdown?.length && userMarkdown !== "Start Conversation") {
                transcript += `User: ${userMarkdown}\n\n`;
            }
            if (botMarkdown?.length) {
                transcript += `Assistant: ${botMarkdown}\n\n`;
            }
        })
        if (transcript?.length) {
            copyText(transcript);
        }

    }

    const renderRequestEventBubbles = (reqId: string, events: any[], idx: number) => {
        if (events?.length && events.every(e => e.eventType === "CallComplete")) {
            return <HiddenDiv key={idx}></HiddenDiv>
        }
        // request recieved on left, assistant speach started and content hit on right
        const requestRecEvents = events.filter(e => e.eventType === "RequestReceived");
        const contentHitEvents = events.filter(e => e.eventType === "ContentHit");
        const userMarkdown = generateUserMarkdown(requestRecEvents, contentHitEvents, idx === 0);
        let botMarkdown = generateBotMarkdown(events);
        const orderFailureReasonMarkdown = sparkContainer.inSpark
            ? ''
            : getOrderFailureReasonMarkdown(events, props.events);
        if (orderFailureReasonMarkdown) {
            botMarkdown = botMarkdown && botMarkdown !== "Unknown"
                ? `${botMarkdown}\n\n${orderFailureReasonMarkdown}`
                : orderFailureReasonMarkdown;
        }
        const userInvalid = !userMarkdown?.length || userMarkdown === "Unknown";
        const botInvalid = !botMarkdown?.length || botMarkdown === "Unknown";
        if (userInvalid && botInvalid) {
            return <></>
        }
        if (botMarkdown.includes("??")) {
            botMarkdown = botMarkdown.replace("??", "");
        }
        return (
            <RequestDetails key={`${idx}-${reqId}`}>
                {userInvalid ?
                    <></>
                    :
                    <>
                        <LiveChatBubble
                            simple={props.simple}
                            markdown={userMarkdown}
                            userBubbleStyle={props.userBubbleStyle}
                            sender={"user"}
                            showCodeModal={() => props.setEventsToShow(events)}
                            createTestCase={() => handleCreateTestCase(events)}
                            forceMobile={props.mobileBubbles}
                            title={`${contentHitEvents[0]?.response?.data?.title}(${idx})`}
                        />
                    </>
                }
                {botInvalid ?
                    <></>
                    :
                    <>
                        <LiveChatBubble
                            userBubbleStyle={props.userBubbleStyle}
                            forceMobile={props.mobileBubbles}
                            markdown={botMarkdown}
                            sender={"bot"}
                            title={`${contentHitEvents[0]?.response?.data?.title}(${idx})`}
                            simple={props.simple}
                        />
                    </>
                }
            </RequestDetails>
        )
    };

    const generateUserMarkdown = (requestEvents: any[], contentHitEvents: any[], first, simpleOverride = false): string => {
        if (props.simple || simpleOverride) {
            // get last events
            if (requestEvents?.length) {
                const lastRequest = requestEvents[requestEvents.length - 1];
                if (lastRequest.utterance?.length) {
                    return lastRequest.utterance;
                }
            } if (contentHitEvents?.length) {
                const lastContentHit = contentHitEvents[contentHitEvents.length - 1];
                if (lastContentHit?.originalRequest?.nlp?.utterance?.length) {
                    return lastContentHit?.originalRequest?.nlp?.utterance;
                } else if (lastContentHit?.originalRequest?.originalInput?.length) {
                    return lastContentHit?.originalRequest?.originalInput;
                } else if (lastContentHit && !first) {
                    return "Detailed analytics not available"
                }
            }
            return first ? "Start Conversation" : "Unknown";
        }
        let md = "";
        for (let i = 0; i < requestEvents.length; i++) {
            const event = requestEvents[i];
            let utterance = event.utterance;
            if (!utterance?.length) {
                // check if content hit array has same index
                const contentHitEvent = contentHitEvents[i];
                utterance = contentHitEvent?.originalRequest?.nlp?.utterance;
                if (!utterance) {
                    utterance = contentHitEvent?.originalRequest?.originalInput;
                }
                if (!utterance && contentHitEvent && !first) {
                    utterance = "Detailed analytics not available";
                }
            }
            md = `${md}${utterance ?? (first ? "Start Conversation" : "Unknown")}\n\n`;
        }
        return md
    };

    const generateBotMarkdown = (responseEvents: any[], simpleOverride = false): string => {
        const contentHits = responseEvents.filter(e => e.eventType === "ContentHit");
        if (!contentHits?.length && props.requireSpeech) {
            return "Unknown"; // if we don't have a content hit then we won't have an assistant speech started for a full assistant response. 
        }
        if (props.simple || simpleOverride) {
            // get speech started and ended events
            const assistantSpeechStarted = responseEvents.filter(e => e.eventType === "AssistantSpeechStarted");
            const assistantSpeechEnded = responseEvents.filter(e => e.eventType === "AssistantSpeechEnded");

            if (assistantSpeechStarted?.length) {
                const speechTexts = [];
                const validSpeechEvents = [];

                for (const startedEvent of assistantSpeechStarted) {
                    // Find the corresponding ended event
                    const correspondingEndedEvent = assistantSpeechEnded.find(endedEvent =>
                        endedEvent.speechText === startedEvent.speechText &&
                        new Date(endedEvent.eventDate || endedEvent.timestamp).getTime() >
                        new Date(startedEvent.eventDate || startedEvent.timestamp).getTime()
                    );

                    // Include speech text if:
                    // 1. No corresponding ended event found, OR
                    // 2. Corresponding ended event found but early property is not true
                    if (!correspondingEndedEvent || !correspondingEndedEvent.early) {
                        if (startedEvent?.speechText?.length) {
                            validSpeechEvents.push(startedEvent);
                            speechTexts.push(stripSsml(startedEvent.speechText));
                        }
                    }
                }

                // if we found no speech texts that qualify then add the last speechstarted event that we have
                if (speechTexts.length === 0 && assistantSpeechStarted.length > 0) {
                    const lastSpeechStarted = assistantSpeechStarted[assistantSpeechStarted.length - 1];
                    if (lastSpeechStarted?.speechText?.length) {
                        validSpeechEvents.push(lastSpeechStarted);
                        speechTexts.push(stripSsml(lastSpeechStarted.speechText));
                    }
                }

                if (speechTexts.length > 0) {
                    const noVoiceTagUsed = validSpeechEvents.some(e => !e.speechText?.includes("vfyvoice"));
                    // check if all speechTexts are teh same
                    const sameSpeechTexts = speechTexts.every(text => text === speechTexts[0]);
                    // Add timeline prefixes if there are multiple speech events
                    if (validSpeechEvents.length > 1 && noVoiceTagUsed) {
                        const firstEventTime = new Date(validSpeechEvents[0].eventDate || validSpeechEvents[0].timestamp).getTime();
                        const timedSpeechTexts = validSpeechEvents.map((event, index) => {
                            if (index === 0) {
                                return `${stripSsml(event.speechText)}`;
                                return `${stripSsml(event.speechText)} (0s)`;
                            } else {
                                const currentEventTime = new Date(event.eventDate || event.timestamp).getTime();
                                const elapsedSeconds = ((currentEventTime - firstEventTime) / 1000).toFixed(1);
                                // elapsed seconds should be rounded to one decimal place if its .0 then just return seconds
                                if (elapsedSeconds.endsWith('.0')) {
                                    return `${stripSsml(event.speechText)}`;
                                    return `${stripSsml(event.speechText)} (${parseFloat(elapsedSeconds)}s)`;
                                }
                                return `${stripSsml(event.speechText)}`;
                                return `${stripSsml(event.speechText)} (${elapsedSeconds}s)`;
                            }
                        });
                        return timedSpeechTexts.join('\n\n');
                    } else if (validSpeechEvents.length > 1 && !noVoiceTagUsed && sameSpeechTexts) {
                        return speechTexts[0];
                    }
                    else {
                        return speechTexts.join('\n\n');
                    }
                } else if (assistantSpeechStarted.length > 0) {
                    return "Detailed analytics not available";
                }
            }
            if (contentHits?.length && !props.requireSpeech) {
                const lastContentHit = contentHits[contentHits.length - 1];
                if (lastContentHit?.response?.data?.content?.length) {
                    return stripSsml(lastContentHit?.response?.data?.content);
                } else if (lastContentHit) {
                    return "Detailed analytics not available"
                }
            }
            return "Unknown";
        };
        let md = "";
        if (contentHits?.length) {
            md = `${md}#### Content Hits: ${contentHits.length}\n&nbsp;\n\n`;
        }
        const anyAssistantSpeech = responseEvents.find(e => e.eventType === "AssistantSpeechStarted");
        for (const event of responseEvents) {
            if (event.eventType == "AssistantSpeechEnded") {
                if (event.early) {
                    if (event.secondsSpoken) {
                        // round seconds spoken to one decimal
                        md = `${md}#### Assistant interrupted after ${event?.secondsSpoken?.toFixed(1) ?? "?"} seconds\n&nbsp;\n`;
                    } else {
                        md = `${md}#### Assistant interrupted\n&nbsp;\n`;
                    }
                }
            } else if (event.eventType == "AssistantSpeechStarted") {
                // compare event date of events
                // count number of assistant speech paused after this speech started
                const assistantSpeechPaused = responseEvents.filter(e =>
                    e.eventType === "AssistantSpeechPaused"
                    && e.timestamp > event.timestamp
                    && !responseEvents.some(se =>
                        se.eventType === "AssistantSpeechStarted"
                        && se.timestamp > event.timestamp
                        && se.timestamp < e.timestamp)
                );
                if (assistantSpeechPaused?.length) {
                    md = `${md}#### Assistant Speech (Paused ${assistantSpeechPaused.length} times)\n`
                } else {
                    md = `${md}#### Assistant Speech\n`;
                }
                md = `${md}${stripSsml(event?.speechText ?? "unknown")}\n\n&nbsp;\n`;
            } else if (event.eventType == "ContentHit") {
                if (!anyAssistantSpeech) {
                    md = `${md}${stripSsml(event?.response?.data?.content ?? "unknown")}\n\n&nbsp;\n\n`;
                }
            } else if (event.eventType == "OrderPlaced") {
                md = `${md}#### Order placed with ${event?.totalItems ?? 0} \n&nbsp;\n\n`;
            } else if (event.eventType == "Frustration") {
                md = `${md}#### Frustration detected \n&nbsp;\n\n`;
            } else if (event.eventType == "ItemAddedToOrder") {
                md = `${md}#### Item(s) added to order \n&nbsp;\n\n`;
            } else if (event.eventType == "CallTransferred") {
                md = `${md}#### Call transferred \n&nbsp;\n\n`;
            } else if (event.eventType == "ImmediateTransfer") {
                md = `${md}#### Immediate Transfer \n&nbsp;\n\n`;
            }
        }
        // more exact trim
        while (md.endsWith("\n") || md.endsWith("&nbsp;")) {
            // if md ends with \n, remove it
            if (md.endsWith("\n")) {
                md = md.slice(0, -1);
            }
            // if md ends with &nbsp;, remove it
            if (md.endsWith("&nbsp;")) {
                md = md.slice(0, -6);
            }
        }
        return md;
    };

    const generateCallCompleteMarkdown = (event): string => {
        let callEnder = event.callEnder?.charAt(0).toUpperCase() + event.callEnder?.slice(1);

        // In spark, If duration is less than 20 seconds or callEnder is not available, just say call completed
        // or if callEnder is not available, just say call completed
        if ((sparkContainer.inSpark && (event.duration < 20 || !callEnder)) || !callEnder) {
            return `Call Completed\n\nDuration: ${event.duration} seconds\n\n\n\n`;
        }

        return `Call Ended By ${callEnder}\n\nDuration: ${event.duration} seconds\n\n\n\n`;
    };

    const callComplete = props.events.find(e => e.eventType === "CallComplete");

    const renderConversationCalls = () => {
        if (props.showConversationCalls === false) return null;
        if (conversationCallsLoading) {
            return (
                <ConversationCallsWrapper>
                    <ConversationCallsHeader>Calls in Conversation Thread</ConversationCallsHeader>
                    <ConversationCallsList>
                        <ConversationCallPill clickable={false} themes={['secondary-small']} text="Loading..." onClick={null} type="button" />
                    </ConversationCallsList>
                </ConversationCallsWrapper>
            );
        }
        if (!conversationSessions?.length || conversationSessions.length <= 1) return null;
        const currentSessionId = props.sessionId || props.events.find(e => e.sessionId)?.sessionId;
        return (
            <ConversationCallsWrapper>
                <ConversationCallsHeader>Calls in Conversation Thread ({conversationSessions.length})</ConversationCallsHeader>
                {conversationSwitchingSessionId ? (
                    <ConversationCallsLoader>
                        <Loader static />
                    </ConversationCallsLoader>
                ) : null}
                <ConversationCallsList>
                    {conversationSessions.map((session, idx) => {
                        const isActive = session.sessionId === currentSessionId;
                        const timeLabel = `Call #${idx + 1}`;
                        return (
                            <ConversationCallPill
                                key={session.sessionId}
                                active={isActive}
                                clickable={!!props.onSessionSelect}
                                themes={['secondary-small']}
                                text={timeLabel}
                                onClick={() => {
                                    if (session.sessionId === currentSessionId) return;
                                    setConversationSwitchingSessionId(session.sessionId);
                                    props.onSessionSelect?.(session.sessionId);
                                }}
                                type="button"
                            />
                        );
                    })}
                </ConversationCallsList>
            </ConversationCallsWrapper>
        );
    };

    const loadAppointmentReviewData = async (sessionId: string) => {
        try {
            setAppointmentReviewLoading(true);
            const result = await voicifyApi.getSessionAppointmentReviewData(props.applicationId, sessionId);
            if (result.resultType === 'Ok') {
                setAppointmentReviewData(result.data);
            } else {
                setAppointmentReviewData(null);
            }
        } catch {
            setAppointmentReviewData(null);
        } finally {
            setAppointmentReviewLoading(false);
        }
    };

    const renderAppointmentReviewSummary = () => {
        if (appointmentReviewLoading) {
            return null;
        }

        return <StyledSessionAppointmentReviewSummary data={appointmentReviewData} />;
    };

    const renderInlineAppointmentReviewSummary = () => {
        if (appointmentReviewLoading) {
            return null;
        }

        return <InlineSessionAppointmentReviewSummary data={appointmentReviewData} inline />;
    };

    const handleCopyPhoneNumber = async () => {
        if (!fromNumber?.length || !navigator.clipboard?.writeText) {
            return;
        }

        await navigator.clipboard.writeText(fromNumber);
        setCopiedPhoneNumber(true);

        if (phoneCopyTimeoutRef.current !== null) {
            clearTimeout(phoneCopyTimeoutRef.current);
        }

        phoneCopyTimeoutRef.current = setTimeout(() => setCopiedPhoneNumber(false), 2000);
    };

    return (
        <div>
            {showEditTestCaseModal && (
                <TestCaseEditingModal
                    initialValue={testCaseToEdit}
                    onSave={(tc) => saveTestCase(tc)}
                    onClose={() => setShowEditTestCaseModal(false)}
                    isContentItemView={false} />
            )}
            <RelativeWrapper
                id="session-review"
                key={props?.events[0]?.sessionId}
                style={{ height: props.forcedHeight ? props.forcedHeight : props.manualHeight ? `${props.manualHeight}px` : "500px" }}
                className={`${props.inModal ? ' in-modal' : ""}`}
            >
                {showOrderPlacedConfirmationModal &&
                    <ConfirmationDialog
                        title={`Mark Order as Placed`}
                        deleteText="Yes, Mark Order as Placed"
                        onClose={toggleOrderPlacedConfirmationModal}
                        onConfirm={() => markAsOrderPlaced()}
                        warning={true}
                    >
                        <WarningConfirmation
                            text={`Are you sure you want to mark this order as placed?`}
                            useWarningColor={true}
                        />
                    </ConfirmationDialog>
                }
                <HistoryBody
                    className={`chats-history-scrollable-body${props.hideBorder ? ' hide-border' : ""}${props.inModal ? ' in-modal' : ""}`}
                    autoHide
                    autoHeight
                    superSimple={props.superSimple}
                    autoHeightMin={props.forcedHeight ? props.forcedHeight : undefined}
                    autoHeightMax={props.forcedHeight ? props.forcedHeight : `${isMobile ? `calc(100vh - 200px)` : props.inModal ? 'calc(100vh - 280px)' : `calc(100vh - 250px)`}`}
                >
                    <AllConversationsContainer className={`chats-conversations-container scrollable`}>
                        {renderConversationCalls()}
                        <TopLevelDetailsWrapper>
                            {props.superSimple ? <></> : sparkContainer.inSpark ? (
                                // Spark: Inline layout (phone/tags and buttons on same row)
                                <SparkInlineWrapper>
                                    {props.showFrom && fromNumber?.length ? (
                                        <SessionPhoneAndTagsWrapper>
                                            <InlineSessionMetaRow>
                                                <PhoneNumberWrapper>
                                                    {formatPhoneNumber(fromNumber)}
                                                </PhoneNumberWrapper>
                                                <TooltipWrapper text={copiedPhoneNumber ? 'Copied!' : 'Copy phone number'} place="top">
                                                    <PhoneCopyButton type="button" onClick={handleCopyPhoneNumber} copied={copiedPhoneNumber}>
                                                        <PhoneCopyIcon src={copyIcon} alt="" />
                                                    </PhoneCopyButton>
                                                </TooltipWrapper>
                                                {renderInlineAppointmentReviewSummary()}
                                            </InlineSessionMetaRow>
                                            <StyledSessionTags
                                                tags={sessionTags}
                                                featureFlag={featureFlag}
                                                appIds={props.applicationId ? [props.applicationId] : undefined}
                                            />
                                        </SessionPhoneAndTagsWrapper>
                                    ) : (
                                        <SessionPhoneAndTagsWrapper>
                                            {renderInlineAppointmentReviewSummary()}
                                            <StyledSessionTags
                                                tags={sessionTags}
                                                featureFlag={featureFlag}
                                                appIds={props.applicationId ? [props.applicationId] : undefined}
                                            />
                                        </SessionPhoneAndTagsWrapper>
                                    )}
                                    <ButtonsWrapper>
                                        {(orderState && !sessionContainer.simpleView) &&
                                            <TooltipWrapper text={orderState === "orderPlaced" ? "This order has been marked as placed and cannot be updated." : "Click here to mark this order as placed."} place="right">
                                                <div>
                                                    <StyledButton
                                                        loading={analyticsOrderStateContainer.isLoading || sessionContainer.loadingEvents}
                                                        disabled={orderState === "orderPlaced" ? true : false}
                                                        themes={['secondary-small']}
                                                        text={orderState === "orderPlaced" ? "Order Placed" : "Order Not Placed"}
                                                        onClick={() => toggleOrderPlacedConfirmationModal()}
                                                        type="button"
                                                        rightIcon={orderState === "orderPlaced" ? greenCheckIcon : redXIcon}
                                                    />
                                                </div>
                                            </TooltipWrapper>
                                        }
                                        {
                                            (!props?.simple || sparkContainer.inSpark) &&
                                            <StyledButton
                                                disabled={false}
                                                themes={['secondary-small']}
                                                text={sparkContainer.inSpark ? "All Events" : "All Events JSON"}
                                                onClick={() => {
                                                    props.setEventsToShow(props.events);
                                                    if (props.resetCopyButtonText)
                                                        props.resetCopyButtonText();
                                                }}
                                                type="button"
                                            />
                                        }
                                        {
                                            (recordingsAvailable && !props.superSimple) ?
                                                <StyledButton
                                                    disabled={false}
                                                    themes={['secondary-small']}
                                                    text="Recording"
                                                    onClick={() => {
                                                        loadRecording(false);
                                                        if (props?.resetCopyButtonText)
                                                            props?.resetCopyButtonText();
                                                    }}
                                                    type="button"
                                                />
                                                :
                                                <></>
                                        }
                                        {
                                            !props?.superSimple &&
                                            <StyledButton
                                                disabled={false}
                                                themes={['secondary-small']}
                                                text={copyButtonText}
                                                onClick={() => {
                                                    copyTranscript();
                                                    setCopyButtonText("Copied!");
                                                }}
                                                type="button"
                                            />
                                        }
                                    </ButtonsWrapper>
                                </SparkInlineWrapper>
                            ) : sessionContainer.simpleView ? (
                                // Simple view: Stack tags vertically
                                <>
                                    {props.showFrom && fromNumber?.length ? (
                                        <SessionPhoneAndTagsWrapper>
                                            <InlineSessionMetaRow>
                                                <PhoneNumberWrapper>
                                                    {formatPhoneNumber(fromNumber)}
                                                </PhoneNumberWrapper>
                                                <TooltipWrapper text={copiedPhoneNumber ? 'Copied!' : 'Copy phone number'} place="top">
                                                    <PhoneCopyButton type="button" onClick={handleCopyPhoneNumber} copied={copiedPhoneNumber}>
                                                        <PhoneCopyIcon src={copyIcon} alt="" />
                                                    </PhoneCopyButton>
                                                </TooltipWrapper>
                                                {renderInlineAppointmentReviewSummary()}
                                            </InlineSessionMetaRow>
                                            <StyledSessionTags
                                                tags={sessionTags}
                                                featureFlag={featureFlag}
                                                appIds={props.applicationId ? [props.applicationId] : undefined}
                                            />
                                        </SessionPhoneAndTagsWrapper>
                                    ) : (
                                        <SessionPhoneAndTagsWrapper>
                                            {renderInlineAppointmentReviewSummary()}
                                            <StyledSessionTags
                                                tags={sessionTags}
                                                featureFlag={featureFlag}
                                                appIds={props.applicationId ? [props.applicationId] : undefined}
                                            />
                                        </SessionPhoneAndTagsWrapper>
                                    )}
                                    <ButtonsWrapper>
                                        {(orderState && !sessionContainer.simpleView) &&
                                            <TooltipWrapper text={orderState === "orderPlaced" ? "This order has been marked as placed and cannot be updated." : "Click here to mark this order as placed."} place="right">
                                                <div>
                                                    <StyledButton
                                                        loading={analyticsOrderStateContainer.isLoading || sessionContainer.loadingEvents}
                                                        disabled={orderState === "orderPlaced" ? true : false}
                                                        themes={['secondary-small']}
                                                        text={orderState === "orderPlaced" ? "Order Placed" : "Order Not Placed"}
                                                        onClick={() => toggleOrderPlacedConfirmationModal()}
                                                        type="button"
                                                        rightIcon={orderState === "orderPlaced" ? greenCheckIcon : redXIcon}
                                                    />
                                                </div>
                                            </TooltipWrapper>
                                        }
                                        {
                                            (!props?.simple || sparkContainer.inSpark) &&
                                            <StyledButton
                                                disabled={false}
                                                themes={['secondary-small']}
                                                text={sparkContainer.inSpark ? "All Events" : "All Events JSON"}
                                                onClick={() => {
                                                    props.setEventsToShow(props.events);
                                                    if (props.resetCopyButtonText)
                                                        props.resetCopyButtonText();
                                                }}
                                                type="button"
                                            />
                                        }
                                        {
                                            (recordingsAvailable && !props.superSimple) ?
                                                <StyledButton
                                                    disabled={false}
                                                    themes={['secondary-small']}
                                                    text="Recording"
                                                    onClick={() => {
                                                        loadRecording(false);
                                                        if (props?.resetCopyButtonText)
                                                            props?.resetCopyButtonText();
                                                    }}
                                                    type="button"
                                                />
                                                :
                                                <></>
                                        }
                                        {
                                            !props?.superSimple &&
                                            <StyledButton
                                                disabled={false}
                                                themes={['secondary-small']}
                                                text={copyButtonText}
                                                onClick={() => {
                                                    copyTranscript();
                                                    setCopyButtonText("Copied!");
                                                }}
                                                type="button"
                                            />
                                        }
                                    </ButtonsWrapper>
                                </>
                            ) : (
                                // Non-simple view: Position tags to the right of phone number, above buttons
                                <>
                                    <PhoneAndTagsRow>
                                        <InlineSessionMetaRow>
                                            {props.showFrom && fromNumber?.length && (
                                                <PhoneNumberRow>
                                                    <PhoneNumberWrapper>
                                                        {formatPhoneNumber(fromNumber)}
                                                    </PhoneNumberWrapper>
                                                    <TooltipWrapper text={copiedPhoneNumber ? 'Copied!' : 'Copy phone number'} place="top">
                                                        <PhoneCopyButton type="button" onClick={handleCopyPhoneNumber} copied={copiedPhoneNumber}>
                                                            <PhoneCopyIcon src={copyIcon} alt="" />
                                                        </PhoneCopyButton>
                                                    </TooltipWrapper>
                                                </PhoneNumberRow>
                                            )}
                                            {renderInlineAppointmentReviewSummary()}
                                        </InlineSessionMetaRow>
                                        <TagsWrapper>
                                            <StyledSessionTags
                                                tags={sessionTags}
                                                featureFlag={featureFlag}
                                                appIds={props.applicationId ? [props.applicationId] : undefined}
                                            />
                                        </TagsWrapper>
                                    </PhoneAndTagsRow>
                                    <ButtonsWrapper>
                                        {(orderState && !sessionContainer.simpleView) &&
                                            <TooltipWrapper text={orderState === "orderPlaced" ? "This order has been marked as placed and cannot be updated." : "Click here to mark this order as placed."} place="right">
                                                <div>
                                                    <StyledButton
                                                        loading={analyticsOrderStateContainer.isLoading || sessionContainer.loadingEvents}
                                                        disabled={orderState === "orderPlaced" ? true : false}
                                                        themes={['secondary-small']}
                                                        text={orderState === "orderPlaced" ? "Order Placed" : "Order Not Placed"}
                                                        onClick={() => toggleOrderPlacedConfirmationModal()}
                                                        type="button"
                                                        rightIcon={orderState === "orderPlaced" ? greenCheckIcon : redXIcon}
                                                    />
                                                </div>
                                            </TooltipWrapper>
                                        }
                                        {
                                            (!props?.simple || sparkContainer.inSpark) &&
                                            <StyledButton
                                                disabled={false}
                                                themes={['secondary-small']}
                                                text={sparkContainer.inSpark ? "All Events" : "All Events JSON"}
                                                onClick={() => {
                                                    props.setEventsToShow(props.events);
                                                    if (props.resetCopyButtonText)
                                                        props.resetCopyButtonText();
                                                }}
                                                type="button"
                                            />
                                        }
                                        {
                                            (recordingsAvailable && !props.superSimple) ?
                                                <StyledButton
                                                    disabled={false}
                                                    themes={['secondary-small']}
                                                    text="Recording"
                                                    onClick={() => {
                                                        loadRecording(false);
                                                        if (props?.resetCopyButtonText)
                                                            props?.resetCopyButtonText();
                                                    }}
                                                    type="button"
                                                />
                                                :
                                                <></>
                                        }
                                        {
                                            !props?.superSimple &&
                                            <StyledButton
                                                disabled={false}
                                                themes={['secondary-small']}
                                                text={copyButtonText}
                                                onClick={() => {
                                                    copyTranscript();
                                                    setCopyButtonText("Copied!");
                                                }}
                                                type="button"
                                            />
                                        }
                                    </ButtonsWrapper>
                                </>
                            )}
                        </TopLevelDetailsWrapper>
                        {Object.keys(groupedEvents).map((requestId, idx) => {
                            return renderRequestEventBubbles(requestId, groupedEvents[requestId], idx)
                        })}
                        {
                            callComplete && !props.superSimple &&
                            <LiveChatBubble
                                userBubbleStyle={props.userBubbleStyle} markdown={generateCallCompleteMarkdown(callComplete)} sender={"bot"} />
                        }
                    </AllConversationsContainer>
                    {props.inModal ? <AlwaysScrollToBottom /> : <></>}
                </HistoryBody>
                <PageError errors={sessionContainer.error?.length ? [sessionContainer.error] : null} />
                <PageError errors={analyticsOrderStateContainer.errors?.length ? analyticsOrderStateContainer.errors : null} />
            </RelativeWrapper>
        </div>
    )
};

export default SessionReview;
